import { useEffect, useRef } from 'react';

import type { RefObject } from 'react';

/**
 * Égalise, entre les cartes sœurs d'un même conteneur, la hauteur des zones
 * marquées `data-equalize="<group>"` (mesure la hauteur naturelle la plus haute
 * puis l'applique en `min-height` à toutes les zones du groupe).
 *
 * Deux usages des groupes :
 * - `header`/`panel` : cartes dépliables (ex. CardSupport, ArbitrageCards) — on
 *   n'égalise que des zones dont la hauteur naturelle est indépendante de l'état
 *   ouvert/fermé de la carte (l'en-tête, et le contenu du panneau qui reste
 *   mesurable même replié car simplement clippé).
 * - `card` : cartes non dépliables (ex. CardPartner) — toute la carte est une
 *   seule zone équalisée directement, pas de distinction en-tête/panneau.
 *
 * Le recalcul est redéclenché à chaque changement de taille du conteneur
 * (largeur ou hauteur) : ouvrir/fermer une carte redéclenche donc un recalcul,
 * sans perturber l'alignement ni créer de boucle ResizeObserver (voir commentaire
 * plus bas).
 */
const EQUALIZE_GROUPS = ['header', 'panel', 'card'] as const;

const useEqualizeCardHeights = (recomputeKey?: unknown): RefObject<HTMLDivElement | null> => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const equalize = () => {
      for (const group of EQUALIZE_GROUPS) {
        const elements = [...root.querySelectorAll<HTMLElement>(`[data-equalize="${group}"]`)];
        // Reset avant mesure pour retrouver la hauteur naturelle.
        elements.forEach((element) => {
          element.style.minHeight = '';
        });
        if (elements.length < 2) continue;
        const maxHeight = Math.max(...elements.map((element) => element.getBoundingClientRect().height));
        elements.forEach((element) => {
          element.style.minHeight = `${Math.ceil(maxHeight)}px`;
        });
      }
    };

    let cancelled = false;
    equalize();

    // Réaligne une fois les polices web chargées : leur chargement change la hauteur des libellés,
    // ce que l'observer ci-dessous capte déjà, mais on le garde en filet de sécurité explicite.
    void document.fonts?.ready.then(() => {
      if (!cancelled) equalize();
    });

    // Réagit à tout changement de taille du conteneur (largeur re-wrap, ou hauteur suite à un contenu qui
    // se stabilise tardivement). Pas de boucle : `equalize()` reset puis remesure puis réapplique dans le
    // même appel synchrone, donc une fois convergée elle ne produit plus aucune variation nette de taille.
    const observer = new ResizeObserver(() => equalize());
    observer.observe(root);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [recomputeKey]);

  return containerRef;
};

export default useEqualizeCardHeights;
